import React from 'react';
import { ConditionsNew } from '@retail/calculus/types';
import {
  roundingIdSchema,
  RoundingIdSchema,
  SelectPriceRoundingCondition,
} from '@retail/calculus/components';
import { useAppTFunction } from '@retail/app/i18n';
import { useConditionActions } from '@retail/calculus/context';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelectedOrgUnit, useUserBrands } from '@retail/app/stores/selected-context';
import { useFetchPriceRoundingRules } from '@retail/rounding/data-access';
import { RetailTanstackQueryLoadingWrapper } from '@retail/components';
import { PriceRoundingRuleConfig } from '@retail/rounding/types';
import { brandLogo, notBeforeToday } from '@retail/utils';
import { EditConditionDialog } from '../ConditionActionsBase';

interface Props {
  roundingCondition: ConditionsNew;
  onClose: () => void;
}

export const EditRoundingConditionDialog: React.FC<Props> = ({ roundingCondition, onClose }) => {
  const t = useAppTFunction();
  const { createOrUpdateCondition, isLoadingPostChanges } = useConditionActions();
  const { assortmentId } = useSelectedOrgUnit();
  const { selectedBrand } = useUserBrands();

  const loadingState = useFetchPriceRoundingRules({
    assortmentId,
    suspense: false,
  });

  const formMethods = useForm<RoundingIdSchema>({
    resolver: yupResolver(roundingIdSchema(t)),
    defaultValues: { priceRoundingId: roundingCondition.roundingCondition?.ruleSetId },
  });
  const { handleSubmit, watch } = formMethods;

  // A past validFrom is bumped to today; the condition is only sent when the rule actually changes.
  const ruleUnchanged = watch('priceRoundingId') === roundingCondition.roundingCondition?.ruleSetId;

  const onSubmit = handleSubmit(({ priceRoundingId }) => {
    createOrUpdateCondition({
      content: {
        rounding: { roundingRuleSetId: priceRoundingId },
      },
      bindingValue: roundingCondition.getBindingLevel(),
      validFrom: notBeforeToday(roundingCondition.roundingCondition?.validFrom),
    }).then(onClose);
  });

  return (
    <EditConditionDialog
      open
      title={t('calculus.roundingConditions.edit.title', {
        name: roundingCondition.roundingCondition?.ruleSetName,
      })}
      dialogContent={
        <FormProvider {...formMethods}>
          <RetailTanstackQueryLoadingWrapper loadingState={loadingState}>
            {(priceRoundingRules) => (
              <SelectPriceRoundingCondition
                priceRoundingRuleConfigs={priceRoundingRules.map((dto) =>
                  PriceRoundingRuleConfig.fromDto(dto, {
                    name: selectedBrand.name,
                    id: selectedBrand.brandId,
                    imgUrl: brandLogo(selectedBrand.brandId).logo,
                  })
                )}
                existingPriceRoundingCondition={roundingCondition}
              />
            )}
          </RetailTanstackQueryLoadingWrapper>
        </FormProvider>
      }
      isLoading={isLoadingPostChanges}
      isDisabled={ruleUnchanged}
      onSubmit={onSubmit}
      onClose={onClose}
    />
  );
};
